import { Check, ChevronDown, Files, HardDrive, MessageSquare } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import type { PrototypeVersionSummary } from '@/lib/api/versions';
import { formatBytes } from '@/lib/format/bytes';
import { formatAbsoluteTime, formatRelativeTime } from '@/lib/format/date';
import { cn } from '@/lib/utils';

export interface VersionDrawerProps {
  readonly versions: readonly PrototypeVersionSummary[];
  readonly selectedVersionId: string | null;
  readonly isLoading: boolean;
  readonly onSelect: (versionId: string) => void;
}

/**
 * The version picker (spec R3.3): `v3 ▾` in the toolbar, opening a drawer that
 * lists every version with its number, notes, uploader, date, total size, file
 * count and thread count.
 *
 * A drawer rather than a dropdown because of what a version *entry* has to say.
 * Choosing between builds of someone else's work is not choosing from a list of
 * labels — it is reading five hundred characters of release notes and deciding
 * whether the thing you are about to comment on is the thing your colleague
 * meant. A menu item cannot hold that; a drawer row can, and can hold it beside
 * the numbers that tell you whether the upload actually worked.
 *
 * Selecting closes the drawer, because the point of the choice is the thing
 * behind it.
 */
export const VersionDrawer = ({
  versions,
  selectedVersionId,
  isLoading,
  onSelect,
}: VersionDrawerProps) => {
  const [open, setOpen] = useState(false);

  const selected =
    versions.find((version) => version.id === selectedVersionId) ?? null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1 border-viewer-chrome-border bg-transparent px-2.5 font-mono text-xs font-normal text-viewer-chrome-foreground"
          disabled={versions.length === 0}
        >
          {selected === null ? '—' : `v${String(selected.versionNumber)}`}
          <ChevronDown aria-hidden="true" className="size-3.5 opacity-70" />
          <span className="sr-only">Choose a version</span>
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader className="pb-4">
          <SheetTitle>Versions</SheetTitle>
          <SheetDescription>
            Every upload, newest first. Each one is immutable, and the comments
            left on it stay with it.
          </SheetDescription>
        </SheetHeader>

        <div className="-mx-6 flex-1 overflow-y-auto px-6 pb-6">
          {isLoading ? (
            <div className="space-y-3" aria-label="Loading versions">
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} className="h-24 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <ul className="space-y-2">
              {versions.map((version) => {
                const isSelected = version.id === selectedVersionId;

                return (
                  <li key={version.id}>
                    <button
                      type="button"
                      aria-current={isSelected ? 'true' : undefined}
                      className={cn(
                        'w-full rounded-lg border p-3 text-left transition-colors',
                        isSelected
                          ? 'border-primary/40 bg-accent'
                          : 'border-border hover:border-foreground/20 hover:bg-accent/50',
                      )}
                      onClick={() => {
                        onSelect(version.id);
                        setOpen(false);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium text-foreground">
                          v{version.versionNumber}
                        </span>
                        {isSelected ? (
                          <Check
                            aria-hidden="true"
                            className="size-3.5 text-primary"
                          />
                        ) : null}
                        <time
                          className="ml-auto text-xs text-muted-foreground"
                          dateTime={version.createdAt}
                          title={formatAbsoluteTime(version.createdAt)}
                        >
                          {formatRelativeTime(version.createdAt)}
                        </time>
                      </div>

                      <p className="mt-1 text-xs text-muted-foreground">
                        Uploaded by{' '}
                        <span className="text-foreground">
                          {version.createdByName}
                        </span>
                      </p>

                      <p
                        className={cn(
                          'mt-2 line-clamp-3 text-sm',
                          version.notes === null
                            ? 'italic text-muted-foreground'
                            : 'text-foreground',
                        )}
                      >
                        {version.notes ?? 'No notes.'}
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <HardDrive aria-hidden="true" className="size-3" />
                          {formatBytes(version.totalBytes)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Files aria-hidden="true" className="size-3" />
                          {version.fileCount}{' '}
                          {version.fileCount === 1 ? 'file' : 'files'}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare aria-hidden="true" className="size-3" />
                          {version.threadCount}{' '}
                          {version.threadCount === 1 ? 'thread' : 'threads'}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
